/**
 * The Edges tab: every relationship in the model, and how many exist.
 *
 * The table and the editor sit side by side because editing a relationship is
 * almost always a comparison — you are looking at the other five while you
 * decide what this one should be called and what it should carry.
 */

import { useEffect, useMemo, useState } from 'react'
import { EmptyState } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Chip, Field, SearchBox } from '../components'
import { DeleteDialog } from '../components/DeleteDialog'
import { EdgeTypeDialog } from '../nodes/subpages/EdgesPanel'
import { edgeTypesApi } from '../api/brain.api'
import { invalidateModel, useEdgeTypeMutations, useRebuildRelationships } from '../hooks'
import {
  DATA_TYPES,
  type DataType,
  type EdgeTypeDef,
  type GraphModel,
  type NodeTypeDef,
} from '../types/brain.types'

export function EdgesTab({ graphId, model }: { graphId: string; model: GraphModel }) {
  const [creating, setCreating] = useState(false)
  const rebuild = useRebuildRelationships(graphId)
  const toast = useToast()
  const [deleting, setDeleting] = useState<EdgeTypeDef | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return model.edge_types
    return model.edge_types.filter(
      (e) =>
        e.key.toLowerCase().includes(needle) ||
        e.from_node_type.toLowerCase().includes(needle) ||
        e.to_node_type.toLowerCase().includes(needle) ||
        (e.description ?? '').toLowerCase().includes(needle),
    )
  }, [model.edge_types, search])

  // Keep the editor pointed at something real: the first row, unless the one it
  // was showing is still in the model.
  useEffect(() => {
    const exists = model.edge_types.some((e) => e.edge_type_id === selectedId)
    if (!exists) setSelectedId(model.edge_types[0]?.edge_type_id ?? null)
  }, [model.edge_types, selectedId])

  const selected = model.edge_types.find((e) => e.edge_type_id === selectedId)

  return (
    <div className="brain-panel">
      <div className="brain-colstack">
        <div className="cp-toolbar">
          <h1>Edge types</h1>
          <Chip>
            {model.edge_types.length} relationship{model.edge_types.length === 1 ? '' : 's'}
          </Chip>
          <span className="cp-spacer" />
          {model.edge_types.length > 0 && (
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="Search relationships…"
            />
          )}
          {/* Relationships between *records* come from links declared on a
              source. Declaring one after the rows are in cannot reach them, so
              this is how they catch up. */}
          <button
            className="btn btn-sm"
            onClick={async () => {
              try {
                const out = await rebuild.mutateAsync()
                // Names are recomputed in the same pass, so say so — a record
                // that stopped reading as its own id is a visible change the
                // reader would otherwise be left to explain to themselves.
                const detail = [
                  `${out.records} records checked`,
                  `${out.edges} relationships in total`,
                  out.relabelled > 0 ? `${out.relabelled} renamed` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
                toast.success(
                  out.new_edges > 0
                    ? `${out.new_edges} new relationship${out.new_edges === 1 ? '' : 's'}`
                    : 'Nothing new to connect',
                  detail,
                )
              } catch (error) {
                toast.error(error)
              }
            }}
            disabled={rebuild.isPending}
            title="Apply the links declared on your sources to records that are already here."
          >
            {rebuild.isPending ? 'Rebuilding…' : 'Rebuild relationships'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setCreating(true)}
            disabled={model.node_types.length < 1}
          >
            + New edge
          </button>
          <div className="sub2">
            Relationships are first-class — every edge carries its own typed attributes, so the
            link itself can hold facts neither end owns.
          </div>
        </div>

        {model.edge_types.length === 0 ? (
          <EmptyState
            title="No relationships yet"
            action={
              model.node_types.length > 0 ? (
                <button className="btn btn-primary" onClick={() => setCreating(true)}>
                  Add the first one
                </button>
              ) : undefined
            }
          >
            {model.node_types.length === 0
              ? 'Add at least one node type first — a relationship needs two ends.'
              : 'Records will load independently, but nothing will connect them, so no question that spans two types can be answered.'}
          </EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing matches that search">
            Try a different term, or clear the box.
          </EmptyState>
        ) : (
          <div className="cp-split">
            <div className="cp-card cp-tablewrap">
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Relationship</th>
                    <th>From → to</th>
                    <th>Attributes</th>
                    <th>Instances</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((edge) => (
                    <tr
                      key={edge.edge_type_id}
                      data-clickable="true"
                      data-on={edge.edge_type_id === selectedId}
                      onClick={() => setSelectedId(edge.edge_type_id)}
                    >
                      <td className="mono" style={{ color: 'var(--accent)' }}>
                        {edge.key}
                        {edge.description && (
                          <div className="small faint truncate" style={{ maxWidth: 260 }}>
                            {edge.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="truncate">
                          {edge.from_node_type} <span className="faint">→</span>{' '}
                          {edge.to_node_type}
                        </span>
                        <div style={{ marginTop: 3 }}>
                          <Chip tone="plain">N:N</Chip>
                        </div>
                      </td>
                      <td>
                        {edge.attributes.length === 0 ? (
                          <span className="faint small">none</span>
                        ) : (
                          <span className="row-tight" style={{ flexWrap: 'wrap', gap: 4 }}>
                            {edge.attributes.map((a) => (
                              <span key={a.key} className="flag">
                                {a.key}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="num">{(edge.instance_count ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selected && (
              <EdgeEditor
                // Remount on selection so the form is the edge, not the last edit.
                key={selected.edge_type_id}
                graphId={graphId}
                edge={selected}
                nodeTypes={model.node_types}
                onDelete={() => setDeleting(selected)}
              />
            )}
          </div>
        )}
      </div>

      {creating && (
        <EdgeTypeDialog
          graphId={graphId}
          nodeTypes={model.node_types}
          onClose={() => setCreating(false)}
        />
      )}

      {deleting && (
        <DeleteDialog
          title="Delete this relationship"
          name={deleting.key}
          what="relationship"
          previewPath={`/brain/graphs/${graphId}/edge-types/${deleting.edge_type_id}/deletion-preview`}
          onConfirm={() => edgeTypesApi.remove(graphId, deleting.edge_type_id)}
          onDeleted={() => invalidateModel(graphId)}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  )
}

/** The right-hand pane: the selected relationship, editable in place. */
function EdgeEditor({
  graphId,
  edge,
  nodeTypes,
  onDelete,
}: {
  graphId: string
  edge: EdgeTypeDef
  nodeTypes: NodeTypeDef[]
  onDelete: () => void
}) {
  const { update } = useEdgeTypeMutations(graphId)
  const toast = useToast()

  const [key, setKey] = useState(edge.key)
  const [from, setFrom] = useState(edge.from_node_type)
  const [to, setTo] = useState(edge.to_node_type)
  const [description, setDescription] = useState(edge.description ?? '')
  const [attributes, setAttributes] = useState<{ key: string; data_type: DataType }[]>(
    edge.attributes.map((a) => ({ key: a.key, data_type: a.data_type })),
  )

  return (
    <div className="cp-editpane">
      <h2 style={{ fontSize: 14, marginBottom: 14 }}>Edit edge type</h2>

      <Field label="Relationship label">
        <input className="input mono" value={key} onChange={(e) => setKey(e.target.value)} />
      </Field>

      <Field label="Source → target">
        <div className="row-tight">
          <select className="select" value={from} onChange={(e) => setFrom(e.target.value)}>
            {nodeTypes.map((n) => (
              <option key={n.node_type_id} value={n.key}>
                {n.key}
              </option>
            ))}
          </select>
          <span className="faint">→</span>
          <select className="select" value={to} onChange={(e) => setTo(e.target.value)}>
            {nodeTypes.map((n) => (
              <option key={n.node_type_id} value={n.key}>
                {n.key}
              </option>
            ))}
          </select>
        </div>
      </Field>

      {/* No cardinality control. Every relationship here is many-to-many —
          records are matched and merged after the fact, so a rule that said
          "one" could not be enforced without throwing away an edge somebody
          asserted. A select with one valid answer is a question nobody should
          be asked. */}
      <p className="small faint" style={{ margin: '-4px 0 0' }}>
        Relationships are many-to-many. One {from || 'record'} can relate to many{' '}
        {to || 'records'}, and the other way round.
      </p>

      <Field label="Edge attributes">
        <>
          {attributes.map((attribute, index) => (
            <div className="cp-attr" key={index}>
              <input
                className="input"
                style={{ maxWidth: 130, padding: '3px 7px', fontSize: 12 }}
                value={attribute.key}
                aria-label="Attribute name"
                onChange={(e) =>
                  setAttributes((list) =>
                    list.map((a, i) => (i === index ? { ...a, key: e.target.value } : a)),
                  )
                }
              />
              <select
                className="select ty"
                style={{ maxWidth: 110, padding: '3px 7px', fontSize: 11 }}
                value={attribute.data_type}
                aria-label="Attribute type"
                onChange={(e) =>
                  setAttributes((list) =>
                    list.map((a, i) =>
                      i === index ? { ...a, data_type: e.target.value as DataType } : a,
                    ),
                  )
                }
              >
                {DATA_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <button
                className="x"
                aria-label={`Remove ${attribute.key || 'attribute'}`}
                onClick={() => setAttributes((list) => list.filter((_, i) => i !== index))}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            className="cp-addattr"
            onClick={() => setAttributes((list) => [...list, { key: '', data_type: 'string' }])}
          >
            + Add attribute
          </button>
        </>
      </Field>

      <Field label="What does it mean?">
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      <div className="row-tight" style={{ marginTop: 14 }}>
        <button
          className="btn btn-primary"
          style={{ flex: 1 }}
          disabled={update.isPending || !key.trim()}
          onClick={async () => {
            try {
              await update.mutateAsync({
                id: edge.edge_type_id,
                body: {
                  key: key.trim(),
                  from_node_type: from,
                  to_node_type: to,
                  description: description.trim() || null,
                  attributes: attributes.filter((a) => a.key.trim()),
                },
              })
              toast.success(`Saved ${key.trim()}.`)
            } catch (error) {
              toast.error(error)
            }
          }}
        >
          {update.isPending ? 'Saving…' : 'Save edge type'}
        </button>
        <button className="btn" onClick={onDelete}>
          Delete
        </button>
      </div>

      <p className="small faint" style={{ marginTop: 12 }}>
        Attributes live on the relationship itself, so adding one changes neither end's schema.
        {(edge.instance_count ?? 0) > 0 &&
          ` ${edge.instance_count?.toLocaleString()} link${
            edge.instance_count === 1 ? '' : 's'
          } already exist of this type.`}
      </p>
    </div>
  )
}
